import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Building, ChevronDown } from 'lucide-react';
import api from '../services/api';

/**
 * Cascading Project + Work Site selector.
 *
 * Fetches active projects from /projects/ (Tender API source of truth)
 * and work sites from /work-sites/ filtered by the selected project.
 *
 * Props:
 *   projectCode      - controlled value (project_code string or '')
 *   workSiteId       - controlled value (work site id number or null)
 *   onProjectChange  - (projectCode: string) => void
 *   onWorkSiteChange - (workSiteId: number|null) => void
 *   locationNotes    - optional free-text notes value
 *   onLocationNotesChange - optional callback for notes
 *   showNotes        - whether to show the location notes input (default false)
 *   disabled         - disable all inputs
 *   compact          - use smaller styling
 */
const ProjectSiteSelector = ({
  projectCode = '',
  workSiteId = null,
  onProjectChange,
  onWorkSiteChange,
  locationNotes = '',
  onLocationNotesChange,
  showNotes = false,
  disabled = false,
  compact = false,
}) => {
  const [projects, setProjects] = useState([]);
  const [workSites, setWorkSites] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingWorkSites, setLoadingWorkSites] = useState(false);

  // Fetch active projects once on mount
  useEffect(() => {
    let cancelled = false;
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const res = await api.get('/projects/', {
          params: { status: 'active', page_size: 1000 },
        });
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results || [];
        if (!cancelled) setProjects(data);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    };
    fetchProjects();
    return () => { cancelled = true; };
  }, []);

  // Fetch work sites when project changes
  useEffect(() => {
    let cancelled = false;
    if (!projectCode) {
      setWorkSites([]);
      return;
    }
    const fetchWorkSites = async () => {
      setLoadingWorkSites(true);
      try {
        const res = await api.get('/work-sites/', {
          params: { project: projectCode, page_size: 500 },
        });
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results || [];
        if (!cancelled) setWorkSites(data);
      } catch (err) {
        console.error('Failed to fetch work sites:', err);
        if (!cancelled) setWorkSites([]);
      } finally {
        if (!cancelled) setLoadingWorkSites(false);
      }
    };
    fetchWorkSites();
    return () => { cancelled = true; };
  }, [projectCode]);

  const handleProjectChange = useCallback((e) => {
    const code = e.target.value;
    onProjectChange(code);
    onWorkSiteChange(null);
  }, [onProjectChange, onWorkSiteChange]);

  const handleWorkSiteChange = useCallback((e) => {
    const val = e.target.value;
    onWorkSiteChange(val ? parseInt(val, 10) : null);
  }, [onWorkSiteChange]);

  const labelClass = compact
    ? 'block text-xs font-medium text-gray-600 mb-0.5'
    : 'block text-sm font-medium text-gray-700 mb-1';
  const selectClass = compact
    ? 'select-admin w-full text-sm'
    : 'select-admin w-full';
  const inputClass = compact
    ? 'input-admin w-full text-sm'
    : 'input-admin w-full';

  return (
    <div className="space-y-3">
      {/* Project dropdown */}
      <div>
        <label className={labelClass}>
          <MapPin className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-gray-400" />
          Project Location
        </label>
        <div className="relative">
          <select
            value={projectCode}
            onChange={handleProjectChange}
            disabled={disabled || loadingProjects}
            className={selectClass}
          >
            <option value="">
              {loadingProjects ? 'Loading projects...' : '— Select project —'}
            </option>
            {projects.map((p) => (
              <option key={p.project_code} value={p.project_code}>
                {p.project_code} — {p.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Work site dropdown (only if project selected and sites exist) */}
      {projectCode && (
        <div>
          <label className={labelClass}>
            <Building className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5 text-gray-400" />
            Work Site
          </label>
          <select
            value={workSiteId ?? ''}
            onChange={handleWorkSiteChange}
            disabled={disabled || loadingWorkSites || workSites.length === 0}
            className={selectClass}
          >
            <option value="">
              {loadingWorkSites
                ? 'Loading sites...'
                : workSites.length === 0
                ? 'No work sites for this project'
                : '— Select work site (optional) —'}
            </option>
            {workSites.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.site_name}
                {ws.site_type ? ` (${ws.site_type})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Optional location notes */}
      {showNotes && (
        <div>
          <label className={labelClass}>Location Notes</label>
          <input
            type="text"
            value={locationNotes}
            onChange={(e) => onLocationNotesChange?.(e.target.value)}
            disabled={disabled}
            placeholder="e.g. Shelf A3, 2nd floor site office"
            className={inputClass}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectSiteSelector;
